import { ColorV2 } from "config/colors";
import { toRgba } from "config/colors.rgba.logic";
import { TypographyV2 } from "config/font";
import { Link } from "react-router-dom";
import styled from "styled-components";

export const HeadingBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Description = styled.p`
  ${TypographyV2.b2}
  margin: 0;
  color: ${ColorV2.Blue2};
`;

export const OrganizationLink = styled(Link)`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  text-decoration: underline;
  text-underline-offset: 2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const LinkIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
`;

export const ControlsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
`;

export const OptionsSlot = styled.div`
  margin-left: auto;

  @media (max-width: 900px) {
    margin-left: 0;
  }
`;

export const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 24px;
`;

export const ListSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ListSectionHeader = styled.div`
  ${TypographyV2.b2}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${ColorV2.Blue6};
  color: ${ColorV2.Blue2};
  width: fit-content;
  max-width: 100%;
`;

export const ListSectionCount = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const ListRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StaffRow = styled.button`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 200px 200px 200px 220px;
  align-items: center;
  column-gap: 16px;
  padding: 14px 18px;
  border-radius: 16px;
  background: ${ColorV2.White};
  border: 1px solid transparent;
  width: 100%;
  cursor: pointer;
  text-align: left;
  font: inherit;

  &:hover {
    border-color: ${ColorV2.Blue6};
  }

  &:focus-visible {
    outline: 2px solid ${ColorV2.Blue1};
    outline-offset: 2px;
  }

  @media (max-width: 1280px) {
    grid-template-columns: minmax(250px, 1fr) 185px 185px 185px 200px;
    column-gap: 12px;
  }

  @media (max-width: 1180px) {
    grid-template-columns: minmax(230px, 1fr) 165px 165px 165px 180px;
    column-gap: 8px;
  }

  @media (max-width: 1060px) {
    grid-template-columns: minmax(220px, 1fr) 150px 150px 150px 170px;
    column-gap: 8px;
  }

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
    row-gap: 10px;
    padding: 14px;
  }
`;

export const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const AvatarCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${ColorV2.Blue6};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

export const PersonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const PersonName = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PersonNumber = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const RowAction = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
  min-width: 0;

  & > :last-child {
    flex-shrink: 0;
  }

  @media (max-width: 780px) {
    justify-content: flex-end;
  }
`;

export const InactiveTag = styled.span<{
  $variant?: "default" | "future" | "ended";
}>`
  ${TypographyV2.s1}
  color: ${(props) =>
    props.$variant === "future"
      ? ColorV2.DarkAmbigiousYellow
      : props.$variant === "ended"
        ? ColorV2.NegativeRed
        : ColorV2.Blue3};
  background: ${(props) =>
    props.$variant === "future"
      ? toRgba(ColorV2.DarkAmbigiousYellow, 0.16)
      : props.$variant === "ended"
        ? toRgba(ColorV2.NegativeRed, 0.14)
        : "#d4dbe0"};
  border: none;
  box-shadow: none;
  border-radius: 999px;
  padding: 5px 10px;
  line-height: 1;
  white-space: nowrap;
  min-width: 0;
  max-width: calc(100% - 26px);
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ListMessage = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
  padding: 16px 4px;
`;

export const LoadMoreSentinel = styled.div`
  width: 100%;
  height: 1px;
`;
